import { Box, Text } from '@weareberyl/design-system'
import { useMemo } from 'react'
import client from '~/client'

import Table, { ICustomColumnProps } from '~/components/Table'
import UserLink, { User } from '~/components/UserLink'
import {
  ScheduledZoneSuspensionStatus,
  UserSummaryFieldsFragment,
  UserSummaryFieldsFragmentDoc,
  ZoneSuspensionsListQuery_zone_Zone_scheduled_suspensions_ScheduledZoneSuspensionConnection_nodes_ScheduledZoneSuspension as ZoneSuspensions,
  useZoneSuspensionsListQuery,
} from '~/gql/generated/graphql'
import { formatDatetime } from '~/utils'
import { ReasonTag, SuspensionStatusTag } from './SuspensionStatusTag'

type ScheduledZoneSuspensionsProps = {
  id: string
}

function isNotNull<T>(value: T | null | undefined): value is NonNullable<T> {
  return value !== null
}

const useCachedUser = id => {
  const user = useMemo<UserSummaryFieldsFragment>(() => {
    const res = client.readFragment({
      id: `User:${id}`,
      fragment: UserSummaryFieldsFragmentDoc,
      fragmentName: 'UserSummaryFields',
    })
    return res
  }, [id])

  return user
}

const CachedUserLink = ({ author_id }) => {
  const user = useCachedUser(author_id)
  // If cached author data is available, use that so we can show an email address,
  // or fall back to showing author_id. Both will link to user page, but email reads nicer
  return <UserLink user={{ ...user, id: author_id } as User} />
}

const columns: ICustomColumnProps<ZoneSuspensions>[] = [
  {
    title: 'Status',
    render: (value: ScheduledZoneSuspensionStatus) => (
      <SuspensionStatusTag value={value} />
    ),
    dataIndex: ['schedule_status'],
  },
  {
    title: 'Suspend at',
    dataIndex: ['start_time'],
    render: item => formatDatetime(item),
  },
  {
    title: 'Enable at',
    dataIndex: ['end_time'],
    render: item => formatDatetime(item),
  },
  {
    title: 'Reason',
    dataIndex: ['reason'],
    render: value => <ReasonTag value={value} />,
  },
  { title: 'Notes', dataIndex: ['notes'] },
  {
    title: 'Author',
    render: current => <CachedUserLink author_id={current.author_id} />,
  },
]

export const ScheduledZoneSuspensions = ({
  id,
}: ScheduledZoneSuspensionsProps) => {
  const { data } = useZoneSuspensionsListQuery({
    variables: {
      zone_id: id,
    },
  })

  const filteredSuspensions = useMemo(
    () => (data?.zone?.scheduled_suspensions?.nodes ?? []).filter(isNotNull),
    [data],
  )

  if (!data) {
    return null
  }

  const filteredData = {
    table: { nodes: filteredSuspensions },
  }

  if (filteredData.table.nodes.length < 1) {
    return (
      <Box>
        <Text>No scheduled zone suspensions</Text>
      </Box>
    )
  }

  return <Table<ZoneSuspensions> columns={columns} data={filteredData} />
}

export default ScheduledZoneSuspensions
